import { __PLANET_MEDIA_PLAYER_VERSION__ } from '../constants';
import { AbstractMessageBusTransport } from './abstract-message-bus-transport';
import { BackoffStrategy } from './enums';
import { EventEnvelop, JsonEventSerializer } from './event-sourcing';
import { Guid } from './guid';
import { HttpClient, OutgoingHttpHeaders } from './http-client';
import { MessageBusApiEndpoints } from './message-bus-api-endpoints';
import { MessageBusTransportStats } from './message-bus-transport-stats';
import { HttpResponse, ICommit, IMessageBusTransport, IMessageBusTransportSetup } from './models';

/**
 * mux different streams into a single queue.
 * the server will demux the events into separate streams and store them in the event store
 * we do this to preserve the order of events
 */
export class MessageBusTransportHttp extends AbstractMessageBusTransport implements IMessageBusTransport {

    private backoffStrategy: BackoffStrategy = BackoffStrategy.Exponential;
    private jitter: () => number = function () {
        return Number((Math.random() * 100).toFixed(0));
    };

    private readonly http: HttpClient = new HttpClient();
    private readonly id: string;

    private errorCount: number = 0;
    private successfulRequests: number = 0;
    private successfulRequestsThreshold: number = 3;

    private url: string;
    private token: string;
    private stopped: boolean = false;
    private disposed: boolean = false;
    private flushInterval: number = 2000;
    private writePageSize: number = 100;
    private commitTimer: number = null;
    private endpoints: MessageBusApiEndpoints;

    private version: number = 0;
    private lastCommit: ICommit;

    constructor() {
        super();
        this.id = Guid.v1();
        this.stats = new MessageBusTransportStats();
    }

    stop(): void {
        this.stopped = true;
    }

    send(events: EventEnvelop[]): Promise<boolean> | boolean {
        try {
            // no retry logic when sending beacons. because there's no way to
            // get an ack from the server and retry logic would require too much time
            // to run properly.
            console.debug('sending beacon', JSON.stringify(events, null, 4));
            return navigator.sendBeacon(this.endpoints.track, JsonEventSerializer.serializeEvent<EventEnvelop>(events));
        } catch (e) {
            return new Promise(async (resolve) => {
                try {
                    await this.append(events);
                    await this.commit();
                    await this.flush();
                    resolve(true);
                } catch (e) {
                    resolve(false);
                }
            });
        }

    }

    dispose(): void {
        this.disposed = true;
        this.stop();
        this.inputQueue.clear();
    }

    setup(options: IMessageBusTransportSetup): void {
        super.setup(options);
        this.flushInterval = options.flushInterval || 2000;
        this.url = options.url;
        this.token = options.token || '';
        this.endpoints = new MessageBusApiEndpoints(this.url);
        this.start()
            .catch(error => {
                console.warn(error);
            });
    }

    /**
     * If the error counter is > 0 checks if the number of consecutive successful requests
     * since the last error reached the configured threshold, then resets all the counters
     */
    handleSuccessfulCommit(): void {
        if (this.errorCount > 0) {
            this.successfulRequests++;
            if (this.successfulRequests >= this.successfulRequestsThreshold) {
                // if we had enough successful commits we reset the counters
                // and start sending requests at a normal rate again
                this.resetCounters();
            }
        }
    }

    sendAll(): void {
        const outputBuffer: EventEnvelop[] = [];
        this.outputQueue.getItems().forEach((data: ICommit) => outputBuffer.concat(data.items));
        outputBuffer.concat(this.inputQueue.getItems());
        const result: Promise<boolean> | boolean = this.send(outputBuffer);
        if (typeof result === 'boolean') {
            this.inputQueue.clear();
            this.outputQueue.clear();
        }
        if (typeof result === 'object') {
            if (result.then) {
                result
                    .then()
                    .catch();
            }
        }
    }

    getStats(): MessageBusTransportStats {
        return this.stats;
    }

    async commit(forced: boolean = false): Promise<void> {
        try {
            if (this.stopped) {
                return;
            }
            // if the queue didn't change since the last commit we do nothing here
            // this means that we are already sending the chunk
            const commit: ICommit = this.prepare(this.writePageSize);
            if (commit) {
                await this.flush(commit);
                this.outputQueue.dequeue();
                this.handleSuccessfulCommit();
            }
        } catch (error) {
            console.warn('transport error', error);
            // we short-circuit the retry logic. 4XX errors are client side errors that cannot be recovered.
            // chances are that the client was not configured properly, is sending wrong access tokens or
            // producing malformed messages, thus we cannot recover client-side.
            // This might happen when we update the server and clients with mismatching schemas are still around
            // TODO: Find a way to handle event schema version mismatches during server updates (server-side event upcasting?)
            // TODO: Track this events server side to identify clients out of date.
            if (error.code >= 400 && error.code < 500) {
                console.warn(`Looks like the client was not configured properly. This happens when the server sends responses with 4XX status`);
                this.dispose();
            }
            // increase the error counter only if the commit was not forced. we do this to avoid introducing the backoff logic when multiple streams
            // send requests almost simultaneously. Might happen when the page loads because we send:
            // - ClientSessionResume
            // - BrowsingSessionResume
            // - PageSessionStart
            // - PlayerInstanceCreate
            if (!forced) {
                this.errorCount++;
                // if the service is still down after `maxRetries` (default 5)
                // we stop the polling, looks like it is not recoverable. Data loss
                // is considered OK in this scenario, the services are down for some serious reason
                // and cannot be fixed fast enough
                if (this.errorCount > this.maxRetries) {
                    console.warn(`[Analytics] Max retries reached. Looks like the services is not available at the moment. Events will not be tracked`);
                    this.dispose();
                    return;
                }
            }
        }

        // calculate the timeout applying exponential backoff strategy + jitter make load spikes
        // more sparse
        let timeout: number = this.flushInterval;
        if (this.backoffStrategy == BackoffStrategy.Exponential) {
            timeout = (this.flushInterval * (Math.pow(this.errorCount, 2)) + this.jitter());
            if (this.errorCount > 0) {
                console.warn(`There was an error while tracking playback session. Will retry in ${timeout}ms`);
            }
        }

        // if an external component invokes multiplexer.commit we must destroy pending timers
        // to avoid queuing concurrent commits.
        if (this.commitTimer !== null && typeof this.commitTimer === 'number') {
            self.clearTimeout(this.commitTimer);
        }

        // wait and repeat
        this.commitTimer = self.setTimeout(async () => {
            this.commitTimer = null;
            await this.commit();
            // add jitter to avoid synchrony between clients increasing the load on
            // servers during failures
        }, timeout);
    }

    async start(): Promise<void> {
        if (this.stopped === true) {
            this.stopped = false;
            await this.commit();
        }
    }

    /**
     * Sends `writePageSize` events to the server. If the request is successful removes those events
     * from the internal queue
     */
    async flush(commit?: ICommit): Promise<number> {
        // console.debug('flushing commit', commit.id, 'with', commit.items.length, 'items');
        this.stats.lastActivity = new Date().getTime();
        const chunk: EventEnvelop[] = commit?.items;

        if (this.lastCommit === commit) {
            return 0;
        }

        this.lastCommit = commit;
        // send the last 100 events. The number of events is never that big. We send in average 2-5 events per request
        // let chunk = this.inputQueue.dequeue(this.writePageSize);
        if (chunk?.length > 0) {
            // send the token as an authorization bearer if present
            let headers: OutgoingHttpHeaders = {
                'X-Planet-Tracker-Version': __PLANET_MEDIA_PLAYER_VERSION__,
                'X-Planet-Trace-Id': this.id,
            };
            if (this.token.length > 0) {
                headers['Authorization'] = `Bearer ${this.token}`;
            }
            // if there are events in the queue we send them to the api endpoint
            const response: HttpResponse = await this.http.post(this.endpoints.track, JsonEventSerializer.serializeEvent<EventEnvelop[]>(chunk), headers);
            // if the request failed we throw an exception to handle the error and retry later
            if (!response.isSuccessStatusCode) {
                throw {
                    name: response.statusCode,
                };
            }
            this.stats.transactions.increment(chunk.length);
            return chunk.length;
        }
        return 0;
    }

    async append(events: EventEnvelop[]): Promise<boolean> {
        if (this.disposed) {
            return;
        }
        events.forEach(event => this.inputQueue.enqueue(event));
        return true;
    }

    private prepare(numberOfElements: number): ICommit {
        const items: EventEnvelop[] = this.inputQueue.dequeue(numberOfElements);
        if (items.length > 0) {
            const version: number = this.version++;
            const commit: ICommit = {
                id: Guid.v1(),
                version: version,
                items: items,
            };
            this.outputQueue.enqueue(commit);
            return commit;
        }
        return;
    }

    /**
     * Resets the errorCount as well as the counter of consecutive successful requests
     */
    private resetCounters(): void {
        this.errorCount = 0;
        this.successfulRequests = 0;
    }
}
